import React, { useCallback, useEffect, useState } from 'react'
import KnowService from 'services/KnowService'
import { Know as KnowInterface, KnowTranscript } from 'interfaces/Know'
import { onPageEnter } from 'lib/tracking/pageEnter'
import { trackPromise } from 'react-promise-tracker'
import { ApiError } from 'services/ApiService'
import { toast } from 'react-toastify'
import { ErrorTypes } from 'lib/constants/ErrorTypes'
import Router, { useRouter } from 'next/router'
import { AnalyticsEventKnowPreviewView } from 'lib/constants/AnalyticsEventType'
import { getReferrerScreen, parseUuidFromSlug } from 'lib/formatting/url'
import { trackEvent } from 'lib/tracking/analytics-service'
import { appKnowRoute, knowRoute } from 'lib/constants/routes'
import _useTranslation from 'lib/hooks/_useTranslation'
import DesktopKnowSEOSection from './DesktopKnowSEOSection'
import KnowSEOMeta from './KnowSEOMeta'
import { Flashcard } from 'interfaces/Flashcard'
import MobileKnowSEOSection from './MobileKnowSEOSection'
import { SearchOptions } from 'interfaces/TrackingSnippets'
import { getSearchOption, deleteSearchOption, storeKnowToDownload } from 'lib/tracking/storage'
import styled from 'styled-components'
import theme from 'stories/utils/theme'
import { KnowSeoInfo } from 'interfaces/KnowSeoInfo'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/reducer'
import { ActiveExperiments } from 'lib/features/activeExperiments'
import { RestructuredMain, RestructuredMobile } from 'components/layouts/Layouts.css'

const Container = styled.div`
    background-color: ${theme.colors.white};
    touch-action: pan-x pan-y;
`

interface Props {
    know: KnowInterface | null
    transcript: KnowTranscript | null
    similarKnows: KnowInterface[] | null
    flashcards?: Flashcard[] | null
    knowSeoInfo: KnowSeoInfo
    unblurContentForSmallerMarkets: boolean
    isMobileDevice: boolean | null
    experiments: ActiveExperiments
}

const loadingAreas = {
    know: 'know',
}

function KnowSeoSection(props: Props) {
    const [similarKnows, setSimilarKnows] = useState<KnowInterface[] | null>(props.similarKnows ?? null)
    const [searchOption, setSearchOption] = useState<SearchOptions>()
    const [know, setKnow] = useState<KnowInterface | null>(props.know)
    const router = useRouter()
    const { slug } = router.query
    const { t } = _useTranslation('knows')
    const { t: tCommon } = _useTranslation('common')
    const referrerScreen = getReferrerScreen(router)
    const authenticatedUser = useSelector((state: AppState) => state.user)

    useEffect(() => {
        if (!authenticatedUser || !know) return

        router.push(appKnowRoute(know?.slug, { referrerScreen }))
    }, [authenticatedUser, know, referrerScreen, router])

    useEffect(() => {
        onPageEnter()
    }, [slug])

    const fetchData = useCallback(
        (identifier: string) => {
            const search = getSearchOption()

            trackPromise(
                KnowService.get(identifier)
                    .then((know) => {
                        setKnow(know)
                        KnowService.listSimilarKnowsViaAlgorithm(know.uuid)
                            .then((data) => {
                                data?.length > 0 && setSimilarKnows(data)
                            })
                            .catch((error) => {
                                throw error
                            })

                        const path = knowRoute(know.slug)

                        if (search) {
                            setSearchOption(search)
                            deleteSearchOption()
                        }

                        if (Router.asPath !== path) {
                            Router.replace(knowRoute(know.slug))
                        }
                    })
                    .catch((error) => {
                        if (error instanceof ApiError) {
                            if (error.type === ErrorTypes.NotFound) {
                                toast.error(t('knows/knowNotFoundError'))
                            } else {
                                error.handleUnknownError(tCommon, 'know.get')
                            }
                        } else {
                            throw error
                        }
                    }),
                loadingAreas.know
            )
        },
        [referrerScreen, t, tCommon]
    )

    useEffect(() => {
        if (typeof slug !== 'string') {
            return
        }

        const knowIdentifier = parseUuidFromSlug(slug)
        if (knowIdentifier === null) {
            return
        }

        const search = getSearchOption()
        // Know has already been fetched on server-side
        if (props.know) {
            setKnow(props.know)
            storeKnowToDownload(props.know)
            setSimilarKnows(props.similarKnows)
            props.know.isSEOIndexed &&
                trackEvent(AnalyticsEventKnowPreviewView, { know: props.know, referrerScreen, search })

            if (search) {
                setSearchOption(search)
                deleteSearchOption()
            }

            return
        }

        fetchData(knowIdentifier)
    }, [fetchData, props.know, props.similarKnows, referrerScreen, slug])

    if (!know) return null

    return (
        <Container>
            <KnowSEOMeta knowSeoInfo={props.knowSeoInfo} />

            <RestructuredMobile>
                <MobileKnowSEOSection
                    know={know}
                    similarKnows={similarKnows}
                    transcript={props.transcript}
                    flashcards={props.flashcards}
                    knowSeoInfo={props.knowSeoInfo}
                    unblurContentForSmallerMarkets={props.unblurContentForSmallerMarkets}
                    experiments={props.experiments}
                />
            </RestructuredMobile>

            <RestructuredMain>
                <DesktopKnowSEOSection
                    know={know}
                    similarKnows={similarKnows}
                    search={searchOption}
                    transcript={props.transcript}
                    flashcards={props.flashcards}
                    knowSeoInfo={props.knowSeoInfo}
                    unblurContentForSmallerMarkets={props.unblurContentForSmallerMarkets}
                    experiments={props.experiments}
                />
            </RestructuredMain>
        </Container>
    )
}

export default KnowSeoSection
